import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";

const AboutPage = () => {
  return (
    <>
      <Seo
        title="About — #whatshesaidproject"
        description="Since 2015, #whatshesaidproject has brought people together through storytelling, creative expression, and community engagement. Meet founder Shannon Ivey and the story of the organization."
        path="/about"
      />
      {/* HERO */}
      <section className="bg-white text-[#121212] pt-12 pb-24 md:py-28 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-3">
            About
          </span>
          <h1
            data-testid="about-headline"
            className="font-disp text-[clamp(36px,5vw,72px)] leading-[1.02] tracking-tight font-normal text-[#121212] text-balance max-w-4xl"
          >
            From the street to the statehouse and from the page to the stage.
          </h1>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="bg-white text-[#121212] py-24 md:py-32 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-3">
                Who We Are
              </span>
              <h2 className="font-disp text-[clamp(28px,3.5vw,48px)] leading-[1.05] tracking-tight font-normal text-[#121212]">
                A mission-led creative &amp; cultural organization.
              </h2>
            </div>
            <div className="lg:col-span-8 min-w-0 space-y-6 text-[17px] leading-[1.65] text-[#333333]">
              <p>
                Since 2015, #whatshesaidproject has brought people together through storytelling,
                creative expression, and community engagement. What began as a platform for unheard
                stories and overlooked storytellers has grown into a creative and cultural
                organization dedicated to developing people, their work, and the opportunities that
                help them thrive.
              </p>
              <p className="text-[#555555]">
                Today, we collaborate with mission-led writers, artists, speakers, entrepreneurs,
                advocates, and changemakers to develop their ideas, strengthen their platforms, and
                bring their work into the world.
              </p>
              <p className="text-[#555555]">
                Through coaching, consulting, business development, boutique talent management,
                artistic programming, podcasting, and community engagement, we help people turn
                their ideas and experiences into meaningful work that connects, challenges, and
                inspires.
              </p>
              <p className="font-disp italic text-[#121212]">
                From the street to the statehouse and from the page to the stage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ROOTS & EVOLUTION */}
      <section className="bg-[#fafafa] text-[#121212] py-24 md:py-32 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 pb-6 border-b border-[#121212]">
            <div>
              <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-2">
                Roots &amp; Evolution
              </span>
              <h2 className="font-disp text-[clamp(28px,3.5vw,48px)] leading-[1.05] tracking-tight font-normal text-[#121212]">
                Grassroots beginnings, an expanded platform.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8 min-w-0 space-y-6 text-[17px] leading-[1.65] text-[#333333]">
              <p>
                We started as a grassroots community art project to amplify the oral stories and
                lived experiences of women during a time when there was a great need for a place
                to craft and experience the profound transformation of personal story.
              </p>
              <p className="text-[#555555]">
                We grew into a story slam producing project and a podcast — the first story slam
                series in South Carolina, which won NEA grants and reached national audiences.
              </p>
              <p className="text-[#555555]">
                Today, the organization carries those roots into a multidisciplinary practice:
                coaching, consulting, business development, boutique talent management, podcasting,
                and cultural work — always at the intersection of story, people, and culture.
              </p>
            </div>
            <div className="lg:col-span-4">
              <figure className="relative bg-[#f4f4f4] border border-[#e5e5e5] overflow-hidden aspect-[4/5]">
                <img
                  src="https://images.squarespace-cdn.com/content/v1/609884cf2896e65339ffb681/1621518755454-GAEKGW71WOC18R4KL395/Story+Slam+2017+%2811+of+13%29.jpg"
                  alt="Story slam event, #whatshesaidproject"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <figcaption className="absolute bottom-4 left-4 text-[11px] font-mono uppercase tracking-[.12em] text-[#666666] bg-white/90 px-3 py-1.5 border border-[#e5e5e5]">
                  Story Slam / 2017
                </figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="bg-white text-[#121212] py-24 md:py-32 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-2">
              Founder
            </span>
            <h2 className="font-disp text-[clamp(28px,3.5vw,48px)] leading-[1.05] tracking-tight font-normal text-[#121212] mb-4">
              Shannon Ivey, MFA AEA PCC.
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <figure className="relative bg-[#f4f4f4] border border-[#e5e5e5] overflow-hidden aspect-[3/4]">
                <img
                  src="https://images.squarespace-cdn.com/content/v1/672a48b558540252c5213760/5b08126f-9ffb-4397-bbf2-639dfdfd7710/IMG_0197.jpeg"
                  alt="Shannon Ivey, Founder and Chief Story Officer"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
                <figcaption className="absolute bottom-4 left-4 text-[11px] font-mono uppercase tracking-[.12em] text-[#666666] bg-white/90 px-3 py-1.5 border border-[#e5e5e5]">
                  Shannon Ivey / Founder
                </figcaption>
              </figure>
            </div>
            <div className="lg:col-span-8 min-w-0 space-y-6 text-[17px] leading-[1.65] text-[#333333]">
              <p>
                Shannon Ivey is the Founder and Chief Story Officer of #whatshesaidproject. She
                brings her background as a professional actor, director, theatre professor, and
                applied theatre practitioner — including Theatre of the Oppressed — to
                individuals and organizations who crave the transformation of story work.
              </p>
              <p className="text-[#555555]">
                Shannon has worked with thousands of participants: grassroots social justice
                advocates, professional actors, writers, keynote speakers, leaders, and everyday
                emerging leaders — from the page to the national stage.
              </p>
              <p className="text-[#555555]">
                She specializes in crafting personal narratives. In 2012, she founded the first
                story slam series in South Carolina, the #whatshesaidproject, which won NEA grants
                and grew into a podcast. She is a 2x TEDx speaker and an International Coaching
                Federation Professional Certified Coach, bringing science-based practices to story
                coaching programs, and began doctoral study at the European Graduate School in
                Expressive Arts: Coaching, Consulting and Conflict Transformation in 2024.
              </p>
              <p className="text-[#555555]">
                Her current work includes one-on-one client work, classes and workshops,
                organization and advocacy programming, and{" "}
                <em className="text-[#121212]">
                  Welcome to the Sh*t Show: A Memoir of Colorectal Cancer and the Power of
                  Self-Advocacy
                </em>
                , published by University of South Carolina Press in 2026.
              </p>
              <div className="pt-4">
                <a
                  href="mailto:shannon@whatshesaidproject.com"
                  data-testid="about-founder-email"
                  className="text-xs font-mono uppercase tracking-[.14em] text-[#121212] underline underline-offset-4 hover:text-[#e02020] transition-colors"
                >
                  Email Shannon →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MILESTONES */}
      <section className="bg-[#fafafa] text-[#121212] py-24 md:py-32 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-2">
              Milestones
            </span>
            <h2 className="font-disp text-[clamp(28px,3.5vw,48px)] leading-[1.05] tracking-tight font-normal text-[#121212] mb-4">
              Selected moments along the way.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { year: "2012", label: "Founded as the first story slam series in South Carolina" },
              { year: "2015", label: "Began working with individuals, organizations, and social justice movements" },
              { year: "NEA Grant", label: "Won National Endowment for the Arts grants; grew into a podcast" },
              { year: "2025–26", label: "WRITE HOME anthology in partnership with Athenaeum Press" },
            ].map((m, i) => (
              <div
                key={m.year}
                data-testid={`milestone-${i + 1}`}
                className="border border-[#e5e5e5] bg-white p-8"
              >
                <span className="text-xs font-mono uppercase tracking-[.14em] text-[#e02020] block mb-2">
                  {m.year}
                </span>
                <p className="text-sm text-[#555555] leading-relaxed">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GRANTS & SUPPORT */}
      <section className="bg-white text-[#121212] py-24 md:py-32 border-b border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-2">
              Grants &amp; Support
            </span>
            <h2 className="font-disp text-[clamp(28px,3.5vw,48px)] leading-[1.05] tracking-tight font-normal text-[#121212] mb-4">
              Supported by institutions that believe in the work.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {["South Carolina Arts Commission", "National Endowment for the Arts", "Alternate Roots", "Knight Foundation"].map(
              (name) => (
                <div key={name} className="border border-[#e5e5e5] bg-[#fafafa] p-6 flex items-center justify-center">
                  <span className="text-xs font-mono uppercase tracking-[.14em] text-[#555555] text-center">
                    {name}
                  </span>
                </div>
              )
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#121212] text-white py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-8">
              <span className="text-xs font-mono uppercase tracking-[.18em] text-[#e02020] block mb-3">
                Invitation to Collaborate
              </span>
              <h2 className="font-disp text-[clamp(28px,3.5vw,48px)] leading-[1.05] tracking-tight font-normal text-white mb-6">
                Let us move culture together.
              </h2>
              <p className="text-[17px] text-[#aaaaaa] leading-relaxed max-w-2xl">
                Whether you are an artist seeking coaching and representation, or an organization
                booking keynote talent, we would like to hear from you.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/connect"
                data-testid="about-cta-connect"
                className="inline-flex items-center justify-center px-6 py-3.5 bg-[#e02020] text-white text-xs font-mono uppercase tracking-[.14em] hover:bg-white hover:text-[#121212] transition-colors rounded-none"
              >
                Start an Inquiry →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
